import {AxiosInstance} from 'axios'
import MyAxiosInstanceCarona from './axiosInstanceCarona'
import {
    AtaDetalhe,
    BuscaRecente,
    BuscaRequest,
    BuscaResponse,
    CriarJustificativaRequest,
    DocumentoProcessoOut,
    DocumentoRequest,
    IaTextoOut,
    IaTextoRequest,
    IrpResumo,
    JustificativaOut,
    JustificativaResumo,
    SugestaoDemandaOut,
    VinculoPesquisaOut,
} from '../pages/CaronaInteligente/types'

/**
 * Cliente do carona-service. Busca e detalhe são gratuitos; gerar justificativa
 * custa 1 documento (cobrança server-side; o 406 sinaliza "sem créditos").
 */
export class CaronaApiService {
    private api: AxiosInstance

    constructor() {
        this.api = MyAxiosInstanceCarona
    }

    async buscar(req: BuscaRequest): Promise<BuscaResponse> {
        const {data} = await this.api.post('/atas/buscar', req)
        return data
    }

    /** numero_controle tem barras (rota :path) — NÃO encodar as "/". */
    async detalhe(
        numeroControle: string,
        esferaAderente?: string
    ): Promise<AtaDetalhe> {
        const query = esferaAderente
            ? `?esfera_aderente=${encodeURIComponent(esferaAderente)}`
            : ''
        const {data} = await this.api.get(
            `/atas/${numeroControle}${query}`
        )
        return data
    }

    /** 202 + processamento assíncrono. Lança no 406 (sem créditos). */
    async criarJustificativa(
        req: CriarJustificativaRequest
    ): Promise<JustificativaOut> {
        const {data} = await this.api.post('/justificativas', req)
        return data
    }

    async obterJustificativa(id: string): Promise<JustificativaOut> {
        const {data} = await this.api.get(`/justificativas/${id}`)
        return data
    }

    /** Histórico "Minhas justificativas". */
    async listarJustificativas(): Promise<JustificativaResumo[]> {
        const {data} = await this.api.get('/justificativas')
        return data
    }

    /** Vincula a Pesquisa de Preços (IN 65) criada a partir da justificativa
     * (handoff do módulo — mesmo contrato do Levantamento de Mercado).
     * Idempotente: re-vincular substitui o vínculo. */
    async vincularPesquisa(
        justificativaId: string,
        pesquisaId: string
    ): Promise<VinculoPesquisaOut> {
        const {data} = await this.api.post(
            `/justificativas/${justificativaId}/vincular-pesquisa`,
            {pesquisa_precos_id: pesquisaId}
        )
        return data
    }

    /** "Suas últimas buscas" do usuário (home da busca) — até 8, recentes primeiro. */
    async buscasRecentes(): Promise<BuscaRecente[]> {
        const {data} = await this.api.get('/atas/buscas-recentes')
        return data || []
    }

    /** Sugestões de termo (IA via n8n) quando a busca vem vazia. */
    async sugestoes(termo: string): Promise<string[]> {
        const {data} = await this.api.get(`/atas/sugestoes?termo=${encodeURIComponent(termo)}`)
        return (data && data.sugestoes) || []
    }

    /** Typeahead do campo de busca: objetos sugeridos a partir do PNCP. */
    async autocomplete(q: string): Promise<string[]> {
        const {data} = await this.api.get(`/atas/autocomplete?q=${encodeURIComponent(q)}`)
        return (data && data.sugestoes) || []
    }

    /** IRPs abertas que casam com o objeto (trilho "Participar"; do nosso banco). */
    async buscarIrp(termo: string, uf?: string): Promise<IrpResumo[]> {
        const q = new URLSearchParams({termo})
        if (uf) q.set('uf', uf)
        const {data} = await this.api.get(`/irp?${q.toString()}`)
        return (data && data.resultados) || []
    }

    /** Sugere a redação da "demanda concreta" (entrevista) via IA — 202 + processamento
     * assíncrono (o resultado chega pelo notifier; ver obterSugestaoDemanda). */
    async sugerirDemanda(objeto: string, esfera: string, orgao: string): Promise<SugestaoDemandaOut> {
        const {data} = await this.api.post('/justificativas/sugerir-demanda', {objeto, esfera, orgao})
        return data
    }

    async obterSugestaoDemanda(id: string): Promise<SugestaoDemandaOut> {
        const {data} = await this.api.get(`/justificativas/sugestoes-demanda/${id}`)
        return data
    }

    /** Upload do ETP/DFD/TR (PDF/DOCX ≤10MB): o backend extrai o texto na hora e o
     * documento vira contexto opcional da IA na justificativa (documento_id). */
    async enviarDocumento(file: File): Promise<DocumentoProcessoOut> {
        const form = new FormData()
        form.append('file', file)
        const {data} = await this.api.post('/justificativas/documentos', form, {
            headers: {'Content-Type': 'multipart/form-data'},
        })
        return data
    }

    /** Documentos de planejamento já enviados pelo usuário (org-scoped). */
    async listarDocumentos(): Promise<DocumentoProcessoOut[]> {
        const {data} = await this.api.get('/justificativas/documentos')
        return Array.isArray(data) ? data : []
    }

    /** Dispara a redação da justificativa pela Licito.guru IA — assíncrono: 202
     * {status: PROCESSANDO} e o resultado chega por push (notifier) + GET de
     * detalhe. 409 = já há redação em andamento; 200 CONCLUIDO = já pronta. */
    async redigirJustificativa(req: IaTextoRequest): Promise<IaTextoOut> {
        const {data} = await this.api.post('/justificativas/ia-texto', req)
        return data
    }

    /** Gera o documento (docx | pdf) a partir do conteúdo (editável) e devolve o blob. */
    async gerarDocumento(req: DocumentoRequest, formato: 'docx' | 'pdf' = 'docx'): Promise<Blob> {
        const {data} = await this.api.post(
            `/justificativas/documento?formato=${formato}`, req, {responseType: 'blob'}
        )
        return data
    }
}

export const caronaApi = new CaronaApiService()
